import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { NgClass, NgIf, NgFor } from '@angular/common';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { ToggleService } from '../header/toggle.service';
import { MatExpansionModule } from '@angular/material/expansion';
import { RouterLink, RouterLinkActive, RouterModule } from '@angular/router';
import { FeathericonsModule } from '../../icons/feathericons/feathericons.module';
import { AuthService, User } from '../../services/auth.service';
import { ModuleService, Module } from '../../services/module.service';
import { MenuGroupService } from '../../services/menu-group.service';
import { catchError } from 'rxjs/operators';
import { of, Subscription, forkJoin } from 'rxjs';
import { TRANSPORTISTA_ROLE, transportistaLanding } from '../../guards/transportista-allowlist';

@Component({
    selector: 'app-sidebar',
    imports: [
        NgScrollbarModule,
        MatExpansionModule,
        RouterLinkActive,
        RouterModule,
        RouterLink,
        NgClass,
        NgIf,
        NgFor,
        FeathericonsModule,
    ],
    templateUrl: './sidebar.component.html',
    styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnInit, OnDestroy {
    private subscriptions: Subscription[] = [];

    constructor(
        // eslint-disable-next-line no-unused-vars
        private toggleService: ToggleService,
        private authService: AuthService,
        // eslint-disable-next-line no-unused-vars
        private moduleService: ModuleService,
        // eslint-disable-next-line no-unused-vars
        private menuGroupService: MenuGroupService,
        // eslint-disable-next-line no-unused-vars
        private cdr: ChangeDetectorRef
    ) {
        this.subscriptions.push(
            this.toggleService.isToggled$.subscribe((isToggled) => {
                this.isToggled = isToggled;
            })
        );
    }

    ngOnInit(): void {
        // Suscribirse a los cambios del usuario actual
        this.subscriptions.push(
            this.authService.currentUser$.subscribe((user) => {
                this.currentUser = user;
                // Recargar módulos cuando cambie el usuario
                if (user) {
                    this.loadActiveModules();
                }
            })
        );

        // Suscribirse a cambios en los módulos
        this.subscriptions.push(
            this.moduleService.modulesChanged$.subscribe(() => {
                // Recargar módulos cuando cambien
                this.loadActiveModules();
            })
        );

        // Cargar módulos activos inicialmente
        // No esperamos al usuario porque queremos mostrar todos los módulos activos
        this.loadActiveModules();
    }

    ngOnDestroy(): void {
        // Limpiar suscripciones
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }

    /**
     * Carga los módulos activos desde el backend
     */
    private loadActiveModules(): void {
        forkJoin({
            modules: this.moduleService
                .getModules()
                .pipe(catchError(() => of({ success: false, data: [] }))),
            menuGroups: this.menuGroupService
                .getMenuGroups()
                .pipe(catchError(() => of({ success: false, data: [] }))),
        }).subscribe(({ modules: modRes, menuGroups: mgRes }) => {
            if (modRes.success && modRes.data) {
                this.activeModuleCodes = modRes.data
                    .filter((m) => m.isActive)
                    .map((m) => m.code);
                this.loadDynamicModules(
                    modRes.data,
                    mgRes.success && mgRes.data ? mgRes.data : []
                );
            }
            this.cdr.detectChanges();
        });
    }

    /**
     * Carga y agrupa los módulos dinámicos por grupo de menú.
     * Ordena los grupos según menu_groups.orden. Excluye ADMINISTRACIÓN (bloque fijo al final).
     * IMPORTANTE: Filtra por módulos asignados al usuario actual.
     */
    private loadDynamicModules(
        modules: Module[],
        menuGroups: { nombre: string; orden: number }[]
    ): void {
        // Obtener códigos de módulos habilitados para el usuario actual
        const userEnabledModules = this.currentUser?.modules || [];
        const isAdmin = this.currentUser?.roles?.includes('admin') || false;
        const isConsignatario = this.currentUser?.roles?.includes('consignatario') || false;

        // Filtrar módulos: activos + asignados al usuario (o admin ve todos)
        // Excluir módulos del grupo ADMINISTRACIÓN (se muestran en bloque fijo)
        const userModules = modules.filter((module) => {
            // Solo mostrar módulos activos
            if (!module.isActive) return false;

            // Excluir módulos del grupo ADMINISTRACIÓN
            if (module.menuGroup === 'ADMINISTRACIÓN') return false;

            // Administradores ven todos los módulos activos,
            // excepto kpi-* que requieren asignación individual
            if (isAdmin && !module.code.startsWith('kpi-')) return true;
            if (isAdmin && module.code.startsWith('kpi-')) {
                return userEnabledModules.includes(module.code);
            }

            // Consignatarios solo ven control-dus
            if (isConsignatario) {
                return module.code === 'control-dus';
            }

            // OPB-263: acceso por rol (admin/operator), no por asignación
            // individual — el backend ya filtró por rol antes de incluirlo.
            if (module.accessByRole) {
                return true;
            }

            // Usuarios normales solo ven sus módulos asignados
            if (!userEnabledModules.includes(module.code)) return false;

            return true;
        });

        // Separar módulos sin grupo
        const modulesWithGroup: Module[] = [];
        this.modulesWithoutGroup = [];

        userModules.forEach((module) => {
            // Si no tiene menuGroup, es null, undefined, o está vacío, agregar a módulos sin grupo
            if (
                !module.menuGroup ||
                module.menuGroup === null ||
                module.menuGroup === undefined ||
                (typeof module.menuGroup === 'string' &&
                    module.menuGroup.trim() === '')
            ) {
                this.modulesWithoutGroup.push(module);
            } else {
                modulesWithGroup.push(module);
            }
        });

        // Ordenar módulos sin grupo por order
        this.modulesWithoutGroup.sort((a, b) => a.order - b.order);

        // Agrupar módulos con grupo por menuGroup usando objeto
        // Mapear 'VD' a 'LOGISTIC' para compatibilidad con datos antiguos
        const grouped: { [key: string]: Module[] } = {};

        modulesWithGroup.forEach((module) => {
            // Mapear 'VD' a 'LOGISTIC' para compatibilidad
            let group = module.menuGroup || 'SIN GRUPO';
            if (group === 'VD') {
                group = 'LOGISTIC';
            }
            if (!grouped[group]) {
                grouped[group] = [];
            }
            grouped[group].push(module);
        });

        // Ordenar módulos dentro de cada grupo por order
        Object.keys(grouped).forEach((group) => {
            grouped[group].sort((a, b) => a.order - b.order);
        });

        // Ordenar grupos según menu_groups.orden (ADMINISTRACIÓN no está aquí; va en bloque fijo al final)
        const orderedByOrden = menuGroups
            .slice()
            .sort((a, b) => a.orden - b.orden);
        const orderedGroups: { [key: string]: Module[] } = {};
        const seen = new Set<string>();

        for (const mg of orderedByOrden) {
            if (grouped[mg.nombre]) {
                orderedGroups[mg.nombre] = grouped[mg.nombre];
                seen.add(mg.nombre);
            }
        }
        Object.keys(grouped).forEach((name) => {
            if (!seen.has(name)) {
                orderedGroups[name] = grouped[name];
            }
        });

        this.menuGroupsList = Object.keys(orderedGroups).map((group) => ({
            group,
            modules: orderedGroups[group],
        }));

        // Forzar detección de cambios después de actualizar
        this.cdr.detectChanges();
    }

    // Toggle Service
    isToggled = false;
    toggle() {
        this.toggleService.toggle();
    }

    // Mat Expansion
    panelOpenState = false;

    // Current User
    currentUser: User | null = null;

    // Active modules codes
    activeModuleCodes: string[] = [];

    // Dynamic modules grouped by menuGroup (usamos array de objetos para mejor compatibilidad con templates)
    menuGroupsList: { group: string; modules: Module[] }[] = [];

    // Módulos sin grupo (se muestran después de Inicio)
    modulesWithoutGroup: Module[] = [];

    /**
     * Verifica si el usuario puede ver el menú de usuarios
     */
    canViewUsers(): boolean {
        if (!this.currentUser) return false;

        // Solo administradores pueden ver el menú de usuarios
        return this.currentUser.roles.includes('admin');
    }

    /**
     * Verifica si se debe mostrar el bloque de administración
     * Los usuarios con rol cliente no deben ver este bloque
     */
    canViewAdministrationBlock(): boolean {
        if (!this.currentUser) return false;

        // Los usuarios con rol cliente, consignatario o transportista no pueden ver el bloque de administración
        return !['client', 'consignatario', TRANSPORTISTA_ROLE].some((role) =>
            this.currentUser!.roles.includes(role)
        );
    }

    canViewInicio(): boolean {
        return !['consignatario', TRANSPORTISTA_ROLE].some((role) =>
            this.currentUser?.roles?.includes(role)
        );
    }

    get logoRoute(): string {
        if (this.currentUser?.roles?.includes('consignatario')) return '/control-dus';
        if (this.currentUser?.roles?.includes(TRANSPORTISTA_ROLE)) {
            return transportistaLanding(this.currentUser.modules);
        }
        return '/home';
    }

    /**
     * Verifica si el usuario tiene acceso a un módulo específico
     * El módulo debe estar tanto en la lista del usuario como ser activo
     */
    hasModule(moduleCode: string): boolean {
        if (!this.currentUser) return false;

        // Verificar que el módulo esté en la lista del usuario
        const userHasModule =
            this.currentUser.modules?.includes(moduleCode) || false;

        // Verificar que el módulo esté activo
        const moduleIsActive = this.activeModuleCodes.includes(moduleCode);

        // El módulo debe estar en la lista del usuario Y ser activo
        return userHasModule && moduleIsActive;
    }
}
